import {
  HttpClient,
  HttpContext,
  HttpEvent,
  HttpEventType,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ApiSingleResponse } from '../../interfaces/responses/api-response';
import { SKIP_AUTH } from '../../interceptors/token.interceptor';

/**
 * Profils de dépôt. Doit rester aligné sur l'énumération `UploadPurpose`
 * côté API : la valeur est envoyée telle quelle en query string.
 */
export enum UploadPurpose {
  StudentDocument = 0,
  CourseMaterial = 1,
  SubjectIcon = 2,
  QuestionImport = 3,
  QuestionImage = 4,
  /** Justificatif de virement d'une inscription : image ou PDF, 10 Mo. */
  PaymentProof = 5,
  /** Ressource d'assistance (tutoriel, guide). */
  Assistance = 6,
  /** Photo de profil d'un professeur : images seules, 5 Mo. */
  ProfessorImage = 7,
}

/** Ce que renvoie l'API après un dépôt réussi. */
export interface UploadResult {
  /** Clé opaque à renvoyer lors de la création de l'entité métier. */
  key: string;
  /** URL présignée, utilisable immédiatement pour un aperçu. */
  previewUrl: string;
  contentType: string;
  sizeBytes: number;
  originalFileName: string;
}

/** Autorisation d'écrire un objet, rendue par `/api/uploads/presign`. */
export interface PresignedUpload {
  key: string;
  uploadUrl: string;
  /** À réémettre à l'identique dans l'en-tête du PUT : il est signé. */
  contentType: string;
  expiresAt: string;
  previewUrl: string;
}

/** Progression d'un envoi, pour alimenter une barre. */
export interface UploadProgress {
  /** 0 à 100, ou `null` si la taille totale est inconnue. */
  percent: number | null;
  done: boolean;
  result?: UploadResult;
}

/**
 * Dépôt de fichiers, en deux temps.
 *
 * Le fichier part d'abord seul vers `/api/uploads`, qui le range et rend une
 * clé ; cette clé est ensuite jointe à la création de l'entité métier. Ce
 * découpage évite d'avoir à porter un fichier dans chaque formulaire, au prix
 * d'objets orphelins si l'utilisateur abandonne — que le serveur purge.
 */
@Injectable({ providedIn: 'root' })
export class UploadService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.BASE_URL}/uploads`;

  /** Dépose un fichier et rend la clé, sans suivi de progression. */
  upload(file: File, purpose: UploadPurpose): Observable<UploadResult> {
    return this.http
      .post<ApiSingleResponse<UploadResult>>(this.url(purpose), this.body(file))
      .pipe(map((r) => r.data));
  }

  /**
   * Dépose un fichier en signalant l'avancement. Utile pour un import, où le
   * fichier peut peser plusieurs dizaines de mégaoctets.
   */
  uploadWithProgress(
    file: File,
    purpose: UploadPurpose,
  ): Observable<UploadProgress> {
    return this.http
      .post<ApiSingleResponse<UploadResult>>(this.url(purpose), this.body(file), {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(map((event) => this.toProgress(event)));
  }

  private toProgress(
    event: HttpEvent<ApiSingleResponse<UploadResult>>,
  ): UploadProgress {
    if (event.type === HttpEventType.UploadProgress) {
      return {
        percent: event.total ? Math.round((100 * event.loaded) / event.total) : null,
        done: false,
      };
    }

    if (event.type === HttpEventType.Response) {
      return { percent: 100, done: true, result: event.body?.data };
    }

    return { percent: null, done: false };
  }

  /**
   * Dépose un gros fichier **directement sur le stockage**, en signalant
   * l'avancement.
   *
   * Deux temps : l'API signe une URL d'écriture, puis le navigateur y envoie le
   * fichier. Rien ne transite par l'API — c'est ce qui rend supportable une
   * vidéo de séance de 2 Go, qui autrement mobiliserait une connexion serveur
   * et son disque temporaire pendant tout le transfert.
   *
   * La clé rendue est à réémettre à la création de l'entité métier, exactement
   * comme après un `upload()` classique.
   */
  uploadDirect(
    file: File,
    purpose: UploadPurpose = UploadPurpose.CourseMaterial,
  ): Observable<UploadProgress> {
    const contentType = file.type || 'application/octet-stream';

    return this.http
      .post<ApiSingleResponse<PresignedUpload>>(`${this.baseUrl}/presign?purpose=${purpose}`, {
        fileName: file.name,
        contentType,
        sizeBytes: file.size,
      })
      .pipe(
        switchMap((response) => {
          const presigned = response.data;

          return this.http
            .put(presigned.uploadUrl, file, {
              // Doit correspondre au type signé par le serveur, sinon le
              // stockage rejette le dépôt.
              headers: { 'Content-Type': presigned.contentType },
              // Sans cela, l'intercepteur joindrait le jeton Keycloak et le
              // stockage refuserait la double authentification.
              context: new HttpContext().set(SKIP_AUTH, true),
              reportProgress: true,
              observe: 'events',
            })
            .pipe(map((event) => this.toDirectProgress(event, presigned, file)));
        }),
      );
  }

  private toDirectProgress(
    event: HttpEvent<unknown>,
    presigned: PresignedUpload,
    file: File,
  ): UploadProgress {
    if (event.type === HttpEventType.UploadProgress) {
      return {
        percent: event.total ? Math.round((100 * event.loaded) / event.total) : null,
        done: false,
      };
    }

    if (event.type === HttpEventType.Response) {
      // Le stockage ne renvoie pas de corps exploitable : on reconstitue le
      // résultat à partir de ce que l'on a signé et du fichier envoyé.
      return {
        percent: 100,
        done: true,
        result: {
          key: presigned.key,
          previewUrl: presigned.previewUrl,
          contentType: presigned.contentType,
          sizeBytes: file.size,
          originalFileName: file.name,
        },
      };
    }

    // `Sent` marque le départ du corps ; tous les autres événements
    // (`ResponseHeader`, `DownloadProgress`) surviennent une fois le corps
    // entièrement transmis. Rendre `null` ferait disparaître la barre, et
    // rendre 0 la ferait revenir en arrière juste avant la fin.
    return { percent: event.type === HttpEventType.Sent ? 0 : 100, done: false };
  }

  private url(purpose: UploadPurpose): string {
    return `${this.baseUrl}?purpose=${purpose}`;
  }

  private body(file: File): FormData {
    // Le champ doit s'appeler « file » : c'est le nom du paramètre côté API.
    const form = new FormData();
    form.append('file', file);
    return form;
  }
}
